(function () {
  if (!window.customElements || customElements.get('tech-anchor-nav')) return;

  var instances = new Set();
  var headerOwner = null;
  var activeScroller = null;
  var originalHeaderOffset = null;
  var originalScrollBehavior = null;
  var managedHeaderOffset = 0;

  function scrollY() {
    return Math.max(0, window.pageYOffset || document.documentElement.scrollTop || 0);
  }

  function saveProperty(element, name) {
    return {
      value: element.style.getPropertyValue(name),
      priority: element.style.getPropertyPriority(name)
    };
  }

  function restoreProperty(element, name, saved) {
    if (!saved) return;
    if (saved.value) element.style.setProperty(name, saved.value, saved.priority);
    else element.style.removeProperty(name);
  }

  function scheduleInstances() {
    instances.forEach(function (instance) {
      instance.scheduleUpdate();
    });
  }

  function headerOffset() {
    if (headerOwner) return managedHeaderOffset;
    var header = document.querySelector('.section-header');
    if (header) {
      var rect = header.getBoundingClientRect();
      return Math.max(0, Math.min(header.offsetHeight, rect.bottom));
    }
    return 0;
  }

  function takeHeaderOwnership(instance, hidden) {
    if (headerOwner || !instance.initialized || !instance.sticky) return;
    if (!originalHeaderOffset) {
      originalHeaderOffset = saveProperty(document.documentElement, '--tch-header-offset');
    }
    headerOwner = instance;
    instance.headerHidden = !!hidden;
    instance.lastScrollY = scrollY();
    instance.refreshHeader();
    scheduleInstances();
  }

  function releaseHeaderOwnership(instance) {
    if (headerOwner !== instance) return;
    var hidden = instance.headerHidden;
    instance.releaseHeader();
    headerOwner = null;
    var nextOwner = null;
    instances.forEach(function (candidate) {
      if (!nextOwner && candidate.initialized && candidate.sticky) nextOwner = candidate;
    });
    if (nextOwner) {
      takeHeaderOwnership(nextOwner, hidden);
    } else {
      restoreProperty(document.documentElement, '--tch-header-offset', originalHeaderOffset);
      originalHeaderOffset = null;
      managedHeaderOffset = 0;
      scheduleInstances();
    }
  }

  // Each animation uses instantaneous frame positions, even if a theme enables
  // CSS smooth scrolling. Restore the original inline setting on every exit.
  function takeScrollBehavior() {
    if (!originalScrollBehavior) {
      originalScrollBehavior = saveProperty(document.documentElement, 'scroll-behavior');
      document.documentElement.style.setProperty('scroll-behavior', 'auto', 'important');
    }
  }

  function releaseScrollBehavior() {
    if (activeScroller) return;
    restoreProperty(document.documentElement, 'scroll-behavior', originalScrollBehavior);
    originalScrollBehavior = null;
  }

  function eventElement(event) {
    var target = event.target;
    return target && target.nodeType === 1 ? target : target && target.parentElement;
  }

  class TechAnchorNav extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      if (this.querySelector('nav.tch-nav')) {
        this.initialize();
      } else {
        this.initializeFrame = requestAnimationFrame(() => {
          this.initializeFrame = null;
          this.initialize();
        });
      }
    }

    disconnectedCallback() {
      this.connected = false;
      if (this.initializeFrame) cancelAnimationFrame(this.initializeFrame);
      this.initializeFrame = null;
      this.destroy();
    }

    initialize() {
      if (!this.connected || this.initialized) return;
      this.nav = this.querySelector('nav.tch-nav');
      if (!this.nav) return;
      this.initialized = true;
      this.wrapper = this.closest('.shopify-section') || this;
      this.links = Array.prototype.slice.call(this.nav.querySelectorAll('[data-tech-target]'));
      this.sticky = this.nav.classList.contains('is-sticky');
      this.hadWrapperMarker = this.wrapper.classList.contains('tch-nav-managed');
      this.nav.dataset.bound = 'true';
      if (this.sticky) this.wrapper.classList.add('tch-nav-managed');
      this.lastScrollY = scrollY();
      this.direction = 0;
      this.directionDistance = 0;
      this.listeners = [];
      this.mobileMedia = window.matchMedia('(max-width: 749px)');
      this.reducedMotionMedia = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.listen(this.nav, 'click', this.onClick.bind(this));
      this.listen(window, 'scroll', this.scheduleUpdate.bind(this), { passive: true });
      this.listen(window, 'resize', this.scheduleUpdate.bind(this));
      this.listen(window, 'pageshow', () => {
        this.lastScrollY = scrollY();
        this.direction = 0;
        this.directionDistance = 0;
        this.scheduleUpdate();
      });
      this.listen(document, 'load', this.scheduleUpdate.bind(this), true);
      this.listen(document, 'wheel', this.onInterrupt.bind(this), { passive: true });
      this.listen(document, 'touchstart', this.onInterrupt.bind(this), { passive: true });
      this.listen(document, 'touchmove', this.onInterrupt.bind(this), { passive: true });
      this.listen(document, 'pointerdown', this.onInterrupt.bind(this), { passive: true });
      this.listen(document, 'keydown', this.onInterrupt.bind(this));
      this.listen(document, 'focusin', this.onInteraction.bind(this));
      this.listen(document, 'focusout', this.scheduleUpdate.bind(this));
      this.listen(document, 'shopify:section:load', () => {
        if (headerOwner === this) this.refreshHeader();
        this.scheduleUpdate();
      });
      this.listen(document, 'shopify:section:unload', (event) => {
        if (event.target === this.wrapper) {
          this.destroy();
        } else if (headerOwner === this && this.header && event.target.contains(this.header)) {
          this.releaseHeader();
          this.headerReloadFrame = requestAnimationFrame(() => {
            this.headerReloadFrame = null;
            if (this.initialized && headerOwner === this) this.refreshHeader();
          });
        }
      });

      if (window.ResizeObserver) {
        this.navResizeObserver = new ResizeObserver(() => this.scheduleUpdate());
        this.navResizeObserver.observe(this.nav);
      }
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => {
          if (this.initialized) this.scheduleUpdate();
        });
      }
      instances.add(this);
      if (this.sticky && !headerOwner) takeHeaderOwnership(this, false);
      this.update();
    }

    listen(target, name, handler, options) {
      target.addEventListener(name, handler, options);
      this.listeners.push(function () {
        target.removeEventListener(name, handler, options);
      });
    }

    destroy() {
      if (!this.initialized) return;
      this.initialized = false;
      this.stopAnimation(false);
      if (this.updateFrame) cancelAnimationFrame(this.updateFrame);
      if (this.headerReloadFrame) cancelAnimationFrame(this.headerReloadFrame);
      this.updateFrame = null;
      this.headerReloadFrame = null;
      this.listeners.forEach(function (remove) { remove(); });
      this.listeners = [];
      if (this.navResizeObserver) this.navResizeObserver.disconnect();
      this.navResizeObserver = null;
      instances.delete(this);
      releaseHeaderOwnership(this);
      this.nav.classList.remove('is-stuck');
      delete this.nav.dataset.bound;
      delete this.nav.dataset.techScrolling;
      if (!this.hadWrapperMarker) this.wrapper.classList.remove('tch-nav-managed');
    }

    refreshHeader() {
      if (!this.initialized || headerOwner !== this) return;
      var nextHeader = document.querySelector('.section-header');
      if (this.header === nextHeader && this.header) {
        this.measureHeader();
        return;
      }
      this.releaseHeader();
      this.header = nextHeader;
      if (!this.header) {
        this.headerHeight = 0;
        this.setHeaderState(false);
        return;
      }
      this.headerSnapshot = {
        managed: this.header.classList.contains('tch-header-managed'),
        hidden: this.header.classList.contains('tch-header-hidden'),
        height: saveProperty(this.header, '--tch-managed-header-height')
      };
      this.stickyHeader = this.header.querySelector('sticky-header');
      if (this.stickyHeader) {
        this.stickyHeaderAttribute = this.stickyHeader.getAttribute('data-tech-scroll-managed');
        this.stickyHeader.setAttribute('data-tech-scroll-managed', 'true');
      }
      this.header.classList.add('tch-header-managed');
      if (window.ResizeObserver) {
        this.headerResizeObserver = new ResizeObserver(() => {
          if (!this.initialized || headerOwner !== this) return;
          this.measureHeader();
          scheduleInstances();
        });
        this.headerResizeObserver.observe(this.header);
      }
      if (window.MutationObserver) {
        this.interactionObserver = new MutationObserver((records) => {
          var relevant = records.some((record) => {
            var target = record.target;
            return target === document.body || target === this.header ||
              (this.header && this.header.contains(target)) ||
              (target.closest && target.closest('cart-drawer, cart-notification, .cart-notification, dialog, [role="dialog"]'));
          });
          if (relevant) this.onInteraction();
        });
        this.interactionObserver.observe(document.body, {
          attributes: true,
          subtree: true,
          attributeFilter: ['class', 'open', 'aria-expanded', 'aria-hidden']
        });
      }
      this.measureHeader();
    }

    releaseHeader() {
      if (this.headerResizeObserver) this.headerResizeObserver.disconnect();
      if (this.interactionObserver) this.interactionObserver.disconnect();
      this.headerResizeObserver = null;
      this.interactionObserver = null;
      if (this.header && this.headerSnapshot) {
        this.header.classList.toggle('tch-header-managed', this.headerSnapshot.managed);
        this.header.classList.toggle('tch-header-hidden', this.headerSnapshot.hidden);
        restoreProperty(this.header, '--tch-managed-header-height', this.headerSnapshot.height);
      }
      if (this.stickyHeader) {
        if (this.stickyHeaderAttribute === null) this.stickyHeader.removeAttribute('data-tech-scroll-managed');
        else this.stickyHeader.setAttribute('data-tech-scroll-managed', this.stickyHeaderAttribute);
        this.stickyHeader.currentScrollTop = scrollY();
      }
      this.header = null;
      this.headerSnapshot = null;
      this.stickyHeader = null;
      this.headerHeight = 0;
    }

    measureHeader() {
      this.headerHeight = this.header ? this.header.getBoundingClientRect().height : 0;
      if (this.header) {
        var value = this.headerHeight + 'px';
        if (this.header.style.getPropertyValue('--tch-managed-header-height') !== value) {
          this.header.style.setProperty('--tch-managed-header-height', value);
        }
      }
      this.setHeaderState(this.headerHidden);
    }

    headerProtected() {
      var header = this.header;
      if (!header) return false;
      if (header.contains(document.activeElement) || header.classList.contains('menu-open') ||
          header.querySelector('details[open], dialog[open], [role="dialog"][aria-hidden="false"]')) return true;
      if (document.body.classList.contains('overflow-hidden')) return true;
      var predictiveSearch = header.querySelector('predictive-search');
      if ((predictiveSearch && predictiveSearch.isOpen) || (this.stickyHeader && this.stickyHeader.preventHide)) return true;
      return !!document.querySelector(
        'cart-drawer.active, cart-drawer[open], .cart-notification.active, dialog[open], [role="dialog"][aria-hidden="false"]'
      );
    }

    setHeaderState(hidden) {
      if (headerOwner !== this) return;
      hidden = !!hidden && !!this.header && !this.headerProtected();
      var changed = this.headerHidden !== hidden;
      this.headerHidden = hidden;
      if (this.header) this.header.classList.toggle('tch-header-hidden', hidden);
      var nextOffset = hidden ? 0 : this.headerHeight || 0;
      if (managedHeaderOffset !== nextOffset ||
          document.documentElement.style.getPropertyValue('--tch-header-offset') !== nextOffset + 'px') {
        managedHeaderOffset = nextOffset;
        document.documentElement.style.setProperty('--tch-header-offset', nextOffset + 'px');
        changed = true;
      }
      if (changed) scheduleInstances();
    }

    onInteraction() {
      if (headerOwner === this && this.headerProtected()) {
        this.setHeaderState(false);
        this.direction = 0;
        this.directionDistance = 0;
      }
      this.scheduleUpdate();
    }

    updateHeaderDirection(y) {
      if (headerOwner !== this) return;
      if (!this.header || !this.header.isConnected) this.refreshHeader();
      var delta = y - this.lastScrollY;
      this.lastScrollY = y;
      if (this.headerProtected()) {
        this.setHeaderState(false);
        this.direction = 0;
        this.directionDistance = 0;
        return;
      }
      if (activeScroller) {
        this.direction = 0;
        this.directionDistance = 0;
        return;
      }
      if (y <= (this.headerHeight || 0) + 1) {
        this.setHeaderState(false);
        this.direction = 0;
        this.directionDistance = 0;
        return;
      }
      if (Math.abs(delta) < 0.1) return;
      var direction = delta > 0 ? 1 : -1;
      if (this.direction !== direction) {
        this.direction = direction;
        this.directionDistance = Math.abs(delta);
      } else {
        this.directionDistance += Math.abs(delta);
      }
      if (this.directionDistance < 10) return;
      var atPinPoint = this.wrapper.getBoundingClientRect().top <= headerOffset() + 1;
      if (direction < 0) this.setHeaderState(false);
      else if (atPinPoint) this.setHeaderState(true);
      this.directionDistance = 0;
    }

    prepareHeaderForAnchor(direction, target) {
      if (headerOwner !== this) return;
      this.refreshHeader();
      var targetTop = target.getBoundingClientRect().top + scrollY();
      if (this.headerProtected() || direction < 0 || targetTop <= (this.headerHeight || 0) + this.nav.offsetHeight) {
        this.setHeaderState(false);
      } else if (direction > 0) {
        this.setHeaderState(true);
      }
      this.lastScrollY = scrollY();
      this.direction = 0;
      this.directionDistance = 0;
    }

    scheduleUpdate() {
      if (!this.initialized || this.updateFrame) return;
      this.updateFrame = requestAnimationFrame(() => {
        this.updateFrame = null;
        this.update();
      });
    }

    update() {
      if (!this.initialized) return;
      var y = scrollY();
      this.updateHeaderDirection(y);
      // Keep the floating mobile bar pinned while the site header changes its
      // offset; the wrapper still has to reach the shown header's lower edge.
      var pinOffset = headerOwner ? headerOwner.headerHeight : headerOffset();
      var stuck = this.sticky && this.mobileMedia.matches &&
        this.wrapper.getBoundingClientRect().top <= pinOffset + 0.5;
      this.nav.classList.toggle('is-stuck', stuck);
      if (!this.animation) this.updateActive(y);
    }

    targets() {
      return this.links.map(function (link) {
        return { link: link, el: document.getElementById(link.getAttribute('data-tech-target')) };
      }).filter(function (item) { return item.el; });
    }

    offset() {
      return headerOffset() + (this.sticky ? this.nav.offsetHeight : 0);
    }

    maxScroll() {
      var scrollingElement = document.scrollingElement || document.documentElement;
      return Math.max(0, scrollingElement.scrollHeight - window.innerHeight);
    }

    updateActive(y) {
      var items = this.targets();
      if (!items.length) return;
      var marker = y + this.offset() + 8;
      var current = items[0];
      var currentTop = -Infinity;
      items.forEach(function (item) {
        var top = item.el.getBoundingClientRect().top + y;
        if (top <= marker && top >= currentTop) {
          current = item;
          currentTop = top;
        }
      });
      var last = items[items.length - 1];
      if (this.maxScroll() - y <= 2 && last.el.getBoundingClientRect().top < window.innerHeight) current = last;
      this.setActive(current.link);
    }

    setActive(link) {
      this.links.forEach(function (node) {
        var on = node === link;
        node.classList.toggle('is-active', on);
        if (on) {
          if (node.getAttribute('aria-current') !== 'true') node.setAttribute('aria-current', 'true');
        } else if (node.hasAttribute('aria-current')) {
          node.removeAttribute('aria-current');
        }
      });
    }

    targetScroll(target) {
      var top = target.getBoundingClientRect().top + scrollY() - this.offset() + 4;
      return Math.max(0, Math.min(this.maxScroll(), top));
    }

    writeScroll(top) {
      window.scrollTo({ top: top, left: window.pageXOffset || 0, behavior: 'auto' });
      // Apply mobile pinning in this frame, before the new scroll position is
      // painted. A deferred scroll handler can otherwise leave the bar behind.
      instances.forEach(function (instance) { instance.update(); });
    }

    onClick(event) {
      if (event.defaultPrevented || event.button > 0 || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
      var element = eventElement(event);
      var link = element && element.closest('[data-tech-target]');
      if (!link || !this.nav.contains(link)) return;
      var target = document.getElementById(link.getAttribute('data-tech-target'));
      if (!target) return;
      event.preventDefault();
      if (activeScroller) activeScroller.stopAnimation(false);
      var start = scrollY();
      var preliminaryGoal = this.targetScroll(target);
      var direction = preliminaryGoal > start + 2 ? 1 : preliminaryGoal < start - 2 ? -1 : 0;
      activeScroller = this;
      if (headerOwner) headerOwner.prepareHeaderForAnchor(direction, target);
      this.setActive(link);
      takeScrollBehavior();
      this.nav.dataset.techScrolling = 'true';
      var goal = this.targetScroll(target);
      this.animation = {
        target: target,
        link: link,
        start: start,
        goal: goal,
        startedAt: performance.now(),
        duration: Math.min(1000, Math.max(420, Math.abs(goal - start) * 0.12 + 300))
      };
      if (this.reducedMotionMedia.matches || Math.abs(goal - start) < 1) {
        this.writeScroll(goal);
        this.stopAnimation(false);
        scheduleInstances();
        return;
      }
      this.animationFrame = requestAnimationFrame(this.animate.bind(this));
    }

    animate(time) {
      this.animationFrame = null;
      var animation = this.animation;
      if (!this.initialized || !animation || activeScroller !== this) return;
      if (!animation.target.isConnected) {
        this.stopAnimation(true);
        return;
      }
      if (headerOwner && headerOwner.headerProtected()) headerOwner.setHeaderState(false);
      var goal = this.targetScroll(animation.target);
      // Retarget from the present position if lazy media, resize, or an opened
      // header control changes the destination while scrolling.
      if (Math.abs(goal - animation.goal) > 1) {
        animation.start = scrollY();
        animation.startedAt = time;
        animation.duration = Math.min(650, Math.max(180, Math.abs(goal - animation.start) * 0.12 + 220));
        animation.goal = goal;
      }
      var progress = this.reducedMotionMedia.matches ? 1 : Math.min(1, Math.max(0, (time - animation.startedAt) / animation.duration));
      var eased = progress * progress * (3 - 2 * progress);
      this.writeScroll(animation.start + (goal - animation.start) * eased);
      if (progress >= 1) {
        this.writeScroll(goal);
        this.setActive(animation.link);
        this.stopAnimation(false);
        scheduleInstances();
      } else {
        this.animationFrame = requestAnimationFrame(this.animate.bind(this));
      }
    }

    stopAnimation(updateSpy) {
      if (this.animationFrame) cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
      this.animation = null;
      if (this.nav) delete this.nav.dataset.techScrolling;
      if (activeScroller === this) {
        activeScroller = null;
        if (headerOwner) {
          headerOwner.lastScrollY = scrollY();
          headerOwner.direction = 0;
          headerOwner.directionDistance = 0;
        }
        releaseScrollBehavior();
      }
      if (updateSpy) scheduleInstances();
    }

    onInterrupt(event) {
      if (activeScroller !== this) return;
      var element = eventElement(event);
      if ((event.type === 'pointerdown' || event.type === 'touchstart') &&
          element && element.closest('tech-anchor-nav')) return;
      if (event.type === 'keydown') {
        if (element && element.closest('input, textarea, select, [contenteditable]:not([contenteditable="false"])')) return;
        if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ', 'Spacebar'].indexOf(event.key) === -1) return;
      }
      this.stopAnimation(true);
    }
  }

  customElements.define('tech-anchor-nav', TechAnchorNav);
})();
